import React, { useState, useEffect } from 'react';
import {
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  View,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';

import { createUser, updateUser, getAllPermissions } from '../../../api/User';
import { getBrandsData } from '../../../api/Price';
import BasicModalContainer from '../../../common/components/basicmodal/BasicModalContainer';
import ModalHeader from '../../../common/components/basicmodal/ModalHeader';
import ModalBody from '../../../common/components/basicmodal/ModalBody';
import ModalFooter from '../../../common/components/basicmodal/ModalFooter';
import Select from '../../../common/components/ui/Select';
import { MultiSelect } from 'react-native-element-dropdown';
import { msgStr } from '../../../common/constants/Message';
import { useAlertModal } from '../../../common/hooks';
import { commonModalStyle } from '../../../common/components/basicmodal';

const USER_TYPES = [
  { label: 'Client', value: 1 },
  { label: 'Delivery Driver', value: 2 },
  { label: 'System Admin', value: 3 },
  { label: 'Valet', value: 4 },
];

const AddUserModal = ({
  isModalVisible,
  user,
  setUpdateUserTrigger,
  closeModal,
}) => {
  const isUpdate = !!user;
  const { showAlert } = useAlertModal();
  const [validMessage, setValidMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState<number | string>('');
  const [assignedBrands, setAssignedBrands] = useState<any[]>([]);
  const [restrictedPermissions, setRestrictedPermissions] = useState<any[]>([]);

  const [availableBrands, setAvailableBrands] = useState<any[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<any[]>([]);

  useEffect(() => {
    if (isModalVisible) {
      setValidMessage('');
      setName(user ? user.name : '');
      setEmail(user ? user.email : '');
      setPassword('');
      setUserType(user ? user.userType : '');
      
      // Handle IDs as strings for the dropdown component
      setAssignedBrands(user?.assignedBrands ? user.assignedBrands.map(b => b.id.toString()) : []);
      setRestrictedPermissions(user?.restrictedPermissions ? user.restrictedPermissions.map(p => p.id.toString()) : []);

      fetchOptions();
    }
  }, [isModalVisible, user]);

  const fetchOptions = () => {
    getBrandsData((jsonRes, status) => {
      if (status === 200 && Array.isArray(jsonRes)) {
        setAvailableBrands(jsonRes.map(b => ({ label: b.brand, value: b.id.toString() })));
      }
    });

    getAllPermissions((jsonRes, status) => {
      if (status === 200 && jsonRes.data) {
        setAvailablePermissions(jsonRes.data.map(p => ({ label: p.name, value: p.id.toString() })));
      } else if (status === 200 && Array.isArray(jsonRes)) {
        setAvailablePermissions(jsonRes.map(p => ({ label: p.name, value: p.id.toString() })));
      }
    });
  };

  const handleSave = () => {
    if (!name.trim() || !email.trim()) {
      setValidMessage(msgStr('emptyField'));
      return;
    }

    if (!isUpdate && !password.trim()) {
      setValidMessage('Password is required for new users');
      return;
    }

    setIsLoading(true);

    const payload: any = {
      name,
      email,
      userType: parseInt(userType.toString()),
      assignedBrands: assignedBrands.join(','),
      restrictedPermissions: restrictedPermissions.join(','),
      isVerified: true,
      is_driver: 0,
    };

    if (password.trim()) {
      payload.password = password;
    }

    const handleResponse = (jsonRes, status) => {
      setIsLoading(false);
      switch (status) {
        case 200:
        case 201:
          showAlert('success', jsonRes.message || 'User saved successfully');
          setUpdateUserTrigger(true);
          closeModal();
          break;
        case 409:
          setValidMessage(jsonRes.error || 'Email already exists');
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    };

    if (isUpdate) {
      updateUser(user.id, payload, handleResponse);
    } else {
      createUser(payload, handleResponse);
    }
  };

  return (
    <Modal
      animationType="none"
      transparent={true}
      visible={isModalVisible}
      onRequestClose={closeModal}
    >
      <BasicModalContainer contentStyle={{ width: '90%', maxWidth: 1000 }}>
        <ModalHeader label={isUpdate ? 'Edit User' : 'Add User'} closeModal={closeModal} />
        
        <ModalBody>
          <View style={localStyles.inputRow}>
            <View style={[localStyles.inputContainer, { flex: 1 }]}>
              <Text style={styles.label}>Name</Text>
              <TextInput
                style={[styles.input, { width: '100%' }]}
                placeholder="Name"
                value={name}
                onChangeText={setName}
                placeholderTextColor="#ccc"
              />
            </View>
            <View style={[localStyles.inputContainer, { flex: 1 }]}>
              <Select
                label="User Type"
                options={USER_TYPES}
                value={userType}
                onChange={(val) => setUserType(val)}
                labelField="label"
                valueField="value"
                placeholder="Select User Type"
                customStyle={{
                  input: [styles.input, { width: '100%' }],
                  label: styles.label,
                }}
              />
            </View>
          </View>

          <View style={localStyles.inputRow}>
            <View style={[localStyles.inputContainer, { flex: 1 }]}>
              <Text style={styles.label}>Email</Text>
              <TextInput
                style={[styles.input, { width: '100%' }]}
                placeholder="Email"
                value={email}
                onChangeText={setEmail}
                keyboardType="email-address"
                autoCapitalize="none"
                placeholderTextColor="#ccc"
              />
            </View>
            <View style={[localStyles.inputContainer, { flex: 1 }]}>
              <Text style={styles.label}>
                {isUpdate ? 'New Password (Optional)' : 'Password'}
              </Text>
              <TextInput
                style={[styles.input, { width: '100%' }]}
                placeholder="Password"
                value={password}
                onChangeText={setPassword}
                secureTextEntry={true}
                placeholderTextColor="#ccc"
              />
            </View>
          </View>

          <View style={localStyles.inputRow}>
            <View style={[localStyles.inputContainer, { flex: 1 }]}>
              <Text style={styles.label}>Assigned Brands</Text>
              <MultiSelect
                style={[styles.input, { width: '100%' }]}
                data={availableBrands}
                labelField="label"
                valueField="value"
                placeholder="Select brands"
                searchPlaceholder="Search..."
                value={assignedBrands}
                onChange={(item) => setAssignedBrands(item)}
                selectedStyle={{
                  borderRadius: 12,
                  marginTop: 8,
                  marginRight: 8,
                  backgroundColor: '#e6e6e6',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                }}
              />
            </View>
          </View>

          <View style={localStyles.inputRow}>
            <View style={[localStyles.inputContainer, { flex: 1 }]}>
              <Text style={styles.label}>Restricted Permissions</Text>
              <MultiSelect
                style={[styles.input, { width: '100%' }]}
                data={availablePermissions}
                labelField="label"
                valueField="value"
                placeholder="Select permissions"
                searchPlaceholder="Search..."
                value={restrictedPermissions}
                onChange={(item) => setRestrictedPermissions(item)}
                selectedStyle={{
                  borderRadius: 12,
                  marginTop: 8,
                  marginRight: 8,
                  backgroundColor: '#e6e6e6',
                  paddingHorizontal: 8,
                  paddingVertical: 4,
                }}
              />
            </View>
          </View>

          {validMessage.trim() !== '' && (
            <Text style={styles.message}>{validMessage}</Text>
          )}
        </ModalBody>

        <ModalFooter style={{}}>
          <TouchableOpacity onPress={handleSave}>
            <Text style={styles.addButton}>{isUpdate ? 'Update' : 'Add'}</Text>
          </TouchableOpacity>
        </ModalFooter>
      </BasicModalContainer>
      {isLoading && (
        <View style={styles.overlay}>
          <ActivityIndicator size="large" color="#2e96e1" />
        </View>
      )}
    </Modal>
  );
};

const styles = commonModalStyle;

const localStyles = StyleSheet.create({
  inputRow: {
    flexDirection: 'row',
    gap: 10,
  },
  inputContainer: {
    // marginHorizontal: 4,
    // marginBottom: 4,
  },
});

export default AddUserModal;
